import React from 'react';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import {ICONS} from '../lib/curo-icons.js';
import {cheer} from '../components/cheer/cheer.jsx';
import {injectIntl} from 'react-intl';

/**
 * "AI 학습" 탭 — 티처블머신식 비주얼 ML 트레이너 (이미지 분류).
 *
 * 아키텍처(문서 §0.5): 새 확장 ml2scratch2(큐브로이드 머신러닝 2, ml5 신경망 전이학습)를
 * runtime.getOpcodeFunction 으로 구동(탭↔확장 브리지). 여기서 학습한 모델은 [코드] 탭 블록에서 공유.
 *   train({LABEL}) 샘플추가 · trainModel({EPOCHS,BATCH_SIZE,LEARNING_RATE}) 학습 · getTrainStatus() 상태
 *   getConfidence({LABEL})→0~100 · getPrediction()→"이름 87%" · reset({LABEL:'all'|N}) 초기화
 * i18n: 인라인 로케일 맵(en 기본 + ko + vi, 그 외 en 폴백) — cubroid 확장 i18n 방식과 동일.
 * 설계·단계: docs/ANALYSIS_explainable_ml_tab_2026-07-03.md
 */

const ML_EXT = 'ml2scratch2'; // AI 학습 탭 전용 확장(큐브로이드 머신러닝 2)
const MAX_CLASSES = 10;
const MAX_THUMBS = 120; // 표시(=✕로 지울 수 있는) 썸네일 상한. VM은 전체 샘플 보관, 넘치면 오래된 것부터 표시만 빠짐
const PALETTE = ['#4c97ff', '#ff6680', '#40bf4a', '#ff8c1a', '#9966ff', '#1aa3ff', '#cf63cf', '#0fbd8c', '#ffab19', '#575e75'];

// 다국어 문자열 (en 기본, ko/vi 제공, 그 외 로케일은 en 폴백). 필요 시 언어 키 추가.
const MSG = {
    title: {en: 'AI Learning', ko: 'AI 학습', vi: 'Học AI'},
    sub: {
        en: 'Teach the AI with your webcam → use it right away in [Code] blocks',
        ko: '웹캠으로 직접 가르치고 → [코드] 탭 블록에서 바로 사용',
        vi: 'Dạy AI bằng webcam → dùng ngay trong khối ở tab [Mã lệnh]'
    },
    loadingEngine: {en: 'Loading AI engine…', ko: 'AI 엔진 불러오는 중…', vi: 'Đang tải công cụ AI…'},
    loadingCam: {en: 'Preparing webcam…', ko: '웹캠 준비 중…', vi: 'Đang chuẩn bị webcam…'},
    klass: {en: 'Class', ko: '클래스', vi: 'Lớp'},
    unitImgs: {en: 'imgs', ko: '장', vi: 'ảnh'},
    collectHint: {
        en: 'Press the button below (hold to capture continuously) to collect samples',
        ko: '아래 버튼을 눌러(누르고 있으면 연속) 샘플을 모으세요',
        vi: 'Nhấn nút bên dưới (giữ để chụp liên tục) để thu thập mẫu'
    },
    capture: {en: 'Capture from webcam', ko: '웹캠으로 찍기', vi: 'Chụp từ webcam'},
    upload: {en: 'Upload', ko: '업로드', vi: 'Tải lên'},
    addClass: {en: '＋ Add a class', ko: '＋ 클래스 추가', vi: '＋ Thêm lớp'},
    trainTitle: {en: 'Training', ko: '학습', vi: 'Huấn luyện'},
    trainBtn: {en: 'Train model', ko: '모델 학습시키기', vi: 'Huấn luyện mô hình'},
    advanced: {en: 'Advanced', ko: '고급', vi: 'Nâng cao'},
    epochs: {en: 'Epochs', ko: '에포크', vi: 'Epoch'},
    batchSize: {en: 'Batch size', ko: '배치 크기', vi: 'Kích thước lô'},
    learningRate: {en: 'Learning rate', ko: '학습률', vi: 'Tốc độ học'},
    trainHint: {
        en: 'Neural network (transfer learning) — collect samples, then press Train model',
        ko: '신경망(전이학습) — 샘플 모으고 학습 버튼을 누르세요',
        vi: 'Mạng nơ-ron (học chuyển giao) — thu thập mẫu rồi nhấn Huấn luyện'
    },
    preview: {en: 'Preview', ko: '미리보기', vi: 'Xem trước'},
    nowSeeing: {en: 'Now seeing: ', ko: '지금 보이는 것: ', vi: 'Đang thấy: '},
    showHint: {
        en: 'Capture samples, then show them to the camera',
        ko: '샘플을 찍고 카메라에 비춰보세요',
        vi: 'Chụp mẫu rồi đưa vào camera'
    },
    output: {en: 'Output', ko: '출력', vi: 'Đầu ra'},
    footPre: {
        en: 'The model trained here can be used in the ',
        ko: '여기서 학습한 모델은 [코드] 탭의 ',
        vi: 'Mô hình huấn luyện ở đây có thể dùng trong '
    },
    footExt: {en: 'AI Learning', ko: 'AI 학습', vi: 'Học AI'},
    footPost: {
        en: ' blocks (prediction, confidence, summary) in the [Code] tab. Works with hardware too!',
        ko: ' 블록(예측 결과·신뢰도·학습 요약)에서 그대로 쓸 수 있어요. 하드웨어와도 연동!',
        vi: ' ở tab [Mã lệnh] (kết quả dự đoán, độ tin cậy, tóm tắt). Cũng dùng được với phần cứng!'
    },
    resetAllBtn: {en: 'Reset all', ko: '전체 초기화', vi: 'Đặt lại tất cả'},
    // 학습 상태 (확장 getTrainStatus가 반환하는 코드 → 여기서 현지화)
    training: {en: 'Training…', ko: '학습 중…', vi: 'Đang huấn luyện…'},
    trainDone: {en: 'Training done', ko: '학습 완료', vi: 'Huấn luyện xong'},
    trainReady: {en: 'Ready — press Train model', ko: '준비됨 — 모델 학습시키기', vi: 'Sẵn sàng — nhấn Huấn luyện'},
    collectSamples: {en: 'Collect samples', ko: '샘플을 모으세요', vi: 'Thu thập mẫu'},
    needMore: {en: 'Collect at least {n} photos per class — AI learns better with more examples', ko: '각 클래스에 사진을 {n}장 이상 모아 주세요 — 예시가 많을수록 AI가 똑똑해져요', vi: 'Thu thập ít nhất {n} ảnh mỗi lớp — càng nhiều ví dụ, AI càng thông minh'},
    lowSamples: {en: 'Ready — but more samples = better accuracy ({n}+ per class recommended)', ko: '학습 가능 — 더 모을수록 정확해져요 (클래스당 {n}장 권장)', vi: 'Sẵn sàng — nhưng càng nhiều mẫu càng chính xác (khuyến nghị {n}+ mỗi lớp)'},
    trainErr: {en: 'Training failed — add a few more samples and try again', ko: '학습 실패 — 샘플을 몇 장 더 넣고 다시 눌러 주세요', vi: 'Huấn luyện thất bại — thêm vài mẫu rồi thử lại'},
    // 발산(loss=NaN)은 원인이 명확해서 해결책까지 알려준다. 그냥 "실패"라고만 하면 학생이 뭘 고칠지 모른다.
    trainDiverged: {en: 'Training went wrong — the learning rate is too high. Try 0.0001 in Advanced, then train again', ko: '학습이 잘못됐어요 — 학습률이 너무 커요. 고급에서 0.0001 로 바꾸고 다시 학습해 보세요', vi: 'Huấn luyện bị lỗi — tốc độ học quá cao. Hãy đặt 0.0001 trong Nâng cao rồi thử lại'},
    delClass: {en: 'Delete class', ko: '클래스 삭제', vi: 'Xóa lớp'},
    delPhoto: {en: 'Delete photo', ko: '사진 삭제', vi: 'Xóa ảnh'},
    delConfirm: {en: 'Delete this class and all its photos?', ko: '이 클래스와 사진을 모두 삭제할까요?', vi: 'Xóa lớp này và tất cả ảnh của nó?'},
    // 2026-10-06 직원 의견 "저장/브라우저 저장이 무슨 버튼인지 헷갈림" → 어디에 저장되는지를 이름에 넣고 설명을 붙인다
    saveBtn: {en: 'Save file', ko: '파일로 저장', vi: 'Lưu tệp'},
    loadBtn: {en: 'Open file', ko: '파일 열기', vi: 'Mở tệp'},
    fileTitle: {en: 'File (my computer)', ko: '파일 (내 컴퓨터)', vi: 'Tệp (máy của tôi)'},
    fileHint: {en: 'Downloads the classes and photos as a file. You can open it on another computer too.', ko: '클래스와 사진을 파일로 내려받아요. 다른 컴퓨터에서도 열 수 있어요.', vi: 'Tải các lớp và ảnh về thành tệp. Có thể mở trên máy khác.'},
    bInfo: {en: 'Kept only in this browser on this computer. Name it to keep several.', ko: '이 컴퓨터의 이 브라우저에만 남아요. 이름을 붙여 여러 개 보관할 수 있어요.', vi: 'Chỉ lưu trong trình duyệt này trên máy này. Đặt tên để lưu nhiều mô hình.'},
    loadConfirm: {en: 'Opening replaces the current classes and photos. Continue?', ko: '열면 지금 클래스와 사진이 바뀌어요. 계속할까요?', vi: 'Mở sẽ thay thế các lớp và ảnh hiện tại. Tiếp tục?'},
    loadingData: {en: 'Loading data…', ko: '데이터 불러오는 중…', vi: 'Đang tải dữ liệu…'},
    bTitle: {en: 'Keep in this browser', ko: '이 브라우저에 보관', vi: 'Lưu trong trình duyệt này'},
    bSave: {en: 'Save in this browser', ko: '이 브라우저에 저장', vi: 'Lưu vào trình duyệt này'},
    bNamePrompt: {en: 'Model name?', ko: '모델 이름을 입력하세요', vi: 'Tên mô hình?'},
    bOpen: {en: 'Open', ko: '열기', vi: 'Mở'},
    bDelete: {en: 'Delete', ko: '삭제', vi: 'Xóa'},
    bNone: {en: '(no saved models)', ko: '(저장된 모델 없음)', vi: '(không có mô hình đã lưu)'},
    bDelConfirm: {en: 'Delete this model from the browser?', ko: '이 모델을 보관함에서 삭제할까요?', vi: 'Xóa mô hình này khỏi trình duyệt?'},
    bHint: {en: 'Switch models in [Code] with the "load model" block!', ko: '[코드] 탭의 "모델 불러와 학습하기" 블록으로 전환할 수 있어요!', vi: 'Chuyển mô hình trong [Mã lệnh] bằng khối "tải mô hình"!'},
    preparing: {en: 'Preparing photos… {p}%', ko: '사진 준비 중… {p}%', vi: 'Đang chuẩn bị ảnh… {p}%'},
    maxReached: {en: 'Max {n} photos per class', ko: '클래스당 최대 {n}장까지예요', vi: 'Tối đa {n} ảnh mỗi lớp'},
    mergeBtn: {en: 'Merge', ko: '합치기', vi: 'Gộp'},
    mergeHint: {en: 'Add this model\'s classes to your current ones (same name = combined)', ko: '이 모델의 클래스를 지금 것에 더해요 (이름 같으면 합쳐짐)', vi: 'Thêm các lớp của mô hình này vào lớp hiện tại (cùng tên = gộp)'},
    mergeConfirm: {en: 'Add this model\'s classes to the current ones?', ko: '이 모델의 클래스를 지금 것에 합칠까요?', vi: 'Gộp các lớp của mô hình này vào lớp hiện tại?'}
};

const MSG_EXTRA = {
    "ja": {"title": "AI学習", "sub": "ウェブカメラでAIに直接教えて → [コード]タブのブロックですぐ使えます", "loadingEngine": "AIエンジンを読み込み中…", "loadingCam": "ウェブカメラを準備中…", "klass": "クラス", "unitImgs": "枚", "collectHint": "下のボタンを押して（長押しで連続撮影）サンプルを集めましょう", "capture": "ウェブカメラで撮る", "upload": "アップロード", "addClass": "＋ クラスを追加", "trainTitle": "学習", "trainBtn": "モデルを学習させる", "advanced": "詳細設定", "epochs": "エポック", "batchSize": "バッチサイズ", "learningRate": "学習率", "trainHint": "ニューラルネットワーク（転移学習）— サンプルを集めて「モデルを学習させる」を押してください", "preview": "プレビュー", "nowSeeing": "今見えているもの: ", "showHint": "サンプルを撮って、カメラに見せてみましょう", "output": "出力", "footPre": "ここで学習したモデルは[コード]タブの", "footExt": "AI学習", "footPost": "ブロック（予測結果・信頼度・学習の要約）でそのまま使えます。ハードウェアとも連携！", "resetAllBtn": "全部リセット", "training": "学習中…", "trainDone": "学習完了", "trainReady": "準備完了 — モデルを学習させる を押してください", "collectSamples": "サンプルを集めましょう", "needMore": "各クラスに写真を{n}枚以上集めてください — 例が多いほどAIは賢くなります", "lowSamples": "学習できます — でもサンプルが多いほど正確になります（各クラス{n}枚以上を推奨）", "trainErr": "学習に失敗しました — サンプルを少し増やしてもう一度押してください", "delClass": "クラスを削除", "delPhoto": "写真を削除", "delConfirm": "このクラスと写真をすべて削除しますか？", "saveBtn": "保存", "loadBtn": "開く", "loadConfirm": "開くと今のクラスと写真が置き換わります。続けますか？", "loadingData": "データを読み込み中…", "bTitle": "マイモデル（このブラウザ）", "bSave": "ブラウザに保存", "bNamePrompt": "モデル名は？", "bOpen": "開く", "bDelete": "削除", "bNone": "（保存されたモデルなし）", "bDelConfirm": "このモデルをブラウザから削除しますか？", "bHint": "[コード]タブの「モデルを読み込んで学習する」ブロックで切り替えられます！", "preparing": "写真を準備中… {p}%", "maxReached": "1クラスにつき最大{n}枚までです", "mergeBtn": "統合", "mergeHint": "このモデルのクラスを今のクラスに追加（同じ名前は合体）", "mergeConfirm": "このモデルのクラスを今のクラスに合わせますか？"},
    "zh-cn": {"title": "AI学习", "sub": "用摄像头直接教 AI → 在[代码]标签的积木中马上使用", "loadingEngine": "正在加载 AI 引擎…", "loadingCam": "正在准备摄像头…", "klass": "类别", "unitImgs": "张", "collectHint": "按下方按钮（长按可连续拍摄）来收集样本", "capture": "用摄像头拍摄", "upload": "上传", "addClass": "＋ 添加类别", "trainTitle": "训练", "trainBtn": "训练模型", "advanced": "高级", "epochs": "训练轮数", "batchSize": "批大小", "learningRate": "学习率", "trainHint": "神经网络（迁移学习）— 收集样本后按“训练模型”", "preview": "预览", "nowSeeing": "当前看到: ", "showHint": "拍摄样本后，把它们对准摄像头", "output": "输出", "footPre": "在这里训练的模型可以在[代码]标签的", "footExt": "AI学习", "footPost": "积木（预测结果·置信度·训练摘要）中直接使用。也能配合硬件！", "resetAllBtn": "全部重置", "training": "训练中…", "trainDone": "训练完成", "trainReady": "准备就绪 — 按“训练模型”", "collectSamples": "收集样本", "needMore": "每个类别至少收集 {n} 张照片 — 样本越多，AI 越聪明", "lowSamples": "可以训练 — 但样本越多越准确（建议每个类别 {n} 张以上）", "trainErr": "训练失败 — 多加几个样本后再试一次", "delClass": "删除类别", "delPhoto": "删除照片", "delConfirm": "要删除这个类别和它的所有照片吗？", "saveBtn": "保存", "loadBtn": "打开", "loadConfirm": "打开会替换当前的类别和照片。继续吗？", "loadingData": "正在加载数据…", "bTitle": "我的模型（此浏览器）", "bSave": "保存到浏览器", "bNamePrompt": "模型名称？", "bOpen": "打开", "bDelete": "删除", "bNone": "（没有保存的模型）", "bDelConfirm": "要从浏览器中删除这个模型吗？", "bHint": "在[代码]标签用“载入模型”积木切换模型！", "preparing": "正在准备照片… {p}%", "maxReached": "每个类别最多 {n} 张照片", "mergeBtn": "合并", "mergeHint": "把这个模型的类别加到当前类别（同名则合并）", "mergeConfirm": "要把这个模型的类别合并到当前类别吗？"},
    "zh-tw": {"title": "AI學習", "sub": "用網路攝影機直接教 AI → 在[程式]分頁的積木中馬上使用", "loadingEngine": "正在載入 AI 引擎…", "loadingCam": "正在準備網路攝影機…", "klass": "類別", "unitImgs": "張", "collectHint": "按下方按鈕（長按可連續拍攝）來收集樣本", "capture": "用攝影機拍攝", "upload": "上傳", "addClass": "＋ 新增類別", "trainTitle": "訓練", "trainBtn": "訓練模型", "advanced": "進階", "epochs": "訓練回合", "batchSize": "批次大小", "learningRate": "學習率", "trainHint": "神經網路（遷移學習）— 收集樣本後按「訓練模型」", "preview": "預覽", "nowSeeing": "目前看到: ", "showHint": "拍攝樣本後，把它們對準攝影機", "output": "輸出", "footPre": "在這裡訓練的模型可以在[程式]分頁的", "footExt": "AI學習", "footPost": "積木（預測結果·信心度·訓練摘要）中直接使用。也能搭配硬體！", "resetAllBtn": "全部重設", "training": "訓練中…", "trainDone": "訓練完成", "trainReady": "準備就緒 — 按「訓練模型」", "collectSamples": "收集樣本", "needMore": "每個類別至少收集 {n} 張照片 — 樣本越多，AI 越聰明", "lowSamples": "可以訓練 — 但樣本越多越準確（建議每個類別 {n} 張以上）", "trainErr": "訓練失敗 — 多加幾個樣本後再試一次", "delClass": "刪除類別", "delPhoto": "刪除照片", "delConfirm": "要刪除這個類別和它的所有照片嗎？", "saveBtn": "儲存", "loadBtn": "開啟", "loadConfirm": "開啟會取代目前的類別和照片。要繼續嗎？", "loadingData": "正在載入資料…", "bTitle": "我的模型（此瀏覽器）", "bSave": "儲存到瀏覽器", "bNamePrompt": "模型名稱？", "bOpen": "開啟", "bDelete": "刪除", "bNone": "（沒有儲存的模型）", "bDelConfirm": "要從瀏覽器中刪除這個模型嗎？", "bHint": "在[程式]分頁用「載入模型」積木切換模型！", "preparing": "正在準備照片… {p}%", "maxReached": "每個類別最多 {n} 張照片", "mergeBtn": "合併", "mergeHint": "把這個模型的類別加到目前類別（同名則合併）", "mergeConfirm": "要把這個模型的類別合併到目前類別嗎？"},
    "de": {"title": "AI-Lernen", "sub": "Bringe der AI per Webcam etwas bei → nutze es sofort in den [Code]-Blöcken", "loadingEngine": "AI-Engine wird geladen…", "loadingCam": "Webcam wird vorbereitet…", "klass": "Klasse", "unitImgs": "Bilder", "collectHint": "Drücke die Taste unten (gedrückt halten für Serienaufnahme), um Beispiele zu sammeln", "capture": "Mit Webcam aufnehmen", "upload": "Hochladen", "addClass": "＋ Klasse hinzufügen", "trainTitle": "Training", "trainBtn": "Modell trainieren", "advanced": "Erweitert", "epochs": "Epochen", "batchSize": "Batch-Größe", "learningRate": "Lernrate", "trainHint": "Neuronales Netz (Transfer Learning) — sammle Beispiele und drücke dann „Modell trainieren“", "preview": "Vorschau", "nowSeeing": "Sieht gerade: ", "showHint": "Nimm Beispiele auf und halte sie dann vor die Kamera", "output": "Ausgabe", "footPre": "Das hier trainierte Modell kannst du in den ", "footExt": "AI-Lernen", "footPost": "-Blöcken (Vorhersage, Konfidenz, Zusammenfassung) im [Code]-Tab verwenden. Funktioniert auch mit Hardware!", "resetAllBtn": "Alles zurücksetzen", "training": "Training läuft…", "trainDone": "Training fertig", "trainReady": "Bereit — drücke „Modell trainieren“", "collectSamples": "Beispiele sammeln", "needMore": "Sammle mindestens {n} Fotos pro Klasse — mit mehr Beispielen lernt die AI besser", "lowSamples": "Bereit — aber mehr Beispiele = höhere Genauigkeit ({n}+ pro Klasse empfohlen)", "trainErr": "Training fehlgeschlagen — füge ein paar Beispiele hinzu und versuche es erneut", "delClass": "Klasse löschen", "delPhoto": "Foto löschen", "delConfirm": "Diese Klasse und alle ihre Fotos löschen?", "saveBtn": "Speichern", "loadBtn": "Öffnen", "loadConfirm": "Beim Öffnen werden die aktuellen Klassen und Fotos ersetzt. Fortfahren?", "loadingData": "Daten werden geladen…", "bTitle": "Meine Modelle (dieser Browser)", "bSave": "Im Browser speichern", "bNamePrompt": "Modellname?", "bOpen": "Öffnen", "bDelete": "Löschen", "bNone": "(keine gespeicherten Modelle)", "bDelConfirm": "Dieses Modell aus dem Browser löschen?", "bHint": "Wechsle Modelle in [Code] mit dem Block „Modell laden“!", "preparing": "Fotos werden vorbereitet… {p}%", "maxReached": "Maximal {n} Fotos pro Klasse", "mergeBtn": "Zusammenführen", "mergeHint": "Klassen dieses Modells zu den aktuellen hinzufügen (gleicher Name = kombiniert)", "mergeConfirm": "Klassen dieses Modells zu den aktuellen hinzufügen?"},
    "es": {"title": "Aprendizaje AI", "sub": "Enseña a la AI con tu webcam → úsala enseguida en los bloques de [Código]", "loadingEngine": "Cargando el motor de AI…", "loadingCam": "Preparando la webcam…", "klass": "Clase", "unitImgs": "imág.", "collectHint": "Pulsa el botón de abajo (mantén pulsado para capturar en continuo) para recoger muestras", "capture": "Capturar con la webcam", "upload": "Subir", "addClass": "＋ Añadir una clase", "trainTitle": "Entrenamiento", "trainBtn": "Entrenar modelo", "advanced": "Avanzado", "epochs": "Épocas", "batchSize": "Tamaño de lote", "learningRate": "Tasa de aprendizaje", "trainHint": "Red neuronal (aprendizaje por transferencia) — recoge muestras y pulsa Entrenar modelo", "preview": "Vista previa", "nowSeeing": "Viendo ahora: ", "showHint": "Captura muestras y luego muéstralas a la cámara", "output": "Salida", "footPre": "El modelo entrenado aquí se puede usar en los bloques de ", "footExt": "Aprendizaje AI", "footPost": " (predicción, confianza, resumen) en la pestaña [Código]. ¡También funciona con hardware!", "resetAllBtn": "Reiniciar todo", "training": "Entrenando…", "trainDone": "Entrenamiento completado", "trainReady": "Listo — pulsa Entrenar modelo", "collectSamples": "Recoge muestras", "needMore": "Recoge al menos {n} fotos por clase — con más ejemplos la AI aprende mejor", "lowSamples": "Listo — pero más muestras = más precisión ({n}+ por clase recomendado)", "trainErr": "El entrenamiento falló — añade algunas muestras más e inténtalo de nuevo", "delClass": "Eliminar clase", "delPhoto": "Eliminar foto", "delConfirm": "¿Eliminar esta clase y todas sus fotos?", "saveBtn": "Guardar", "loadBtn": "Abrir", "loadConfirm": "Al abrir se reemplazan las clases y fotos actuales. ¿Continuar?", "loadingData": "Cargando datos…", "bTitle": "Mis modelos (este navegador)", "bSave": "Guardar en el navegador", "bNamePrompt": "¿Nombre del modelo?", "bOpen": "Abrir", "bDelete": "Eliminar", "bNone": "(sin modelos guardados)", "bDelConfirm": "¿Eliminar este modelo del navegador?", "bHint": "¡Cambia de modelo en [Código] con el bloque «cargar modelo»!", "preparing": "Preparando fotos… {p}%", "maxReached": "Máximo {n} fotos por clase", "mergeBtn": "Combinar", "mergeHint": "Añade las clases de este modelo a las actuales (mismo nombre = combinadas)", "mergeConfirm": "¿Combinar las clases de este modelo con las actuales?"},
    "fr": {"title": "Apprentissage AI", "sub": "Apprends à l'AI avec ta webcam → utilise-la aussitôt dans les blocs [Code]", "loadingEngine": "Chargement du moteur AI…", "loadingCam": "Préparation de la webcam…", "klass": "Classe", "unitImgs": "img", "collectHint": "Appuie sur le bouton ci-dessous (maintiens pour capturer en continu) pour collecter des échantillons", "capture": "Capturer avec la webcam", "upload": "Téléverser", "addClass": "＋ Ajouter une classe", "trainTitle": "Entraînement", "trainBtn": "Entraîner le modèle", "advanced": "Avancé", "epochs": "Époques", "batchSize": "Taille du lot", "learningRate": "Taux d'apprentissage", "trainHint": "Réseau de neurones (apprentissage par transfert) — collecte des échantillons puis appuie sur Entraîner le modèle", "preview": "Aperçu", "nowSeeing": "Voit maintenant : ", "showHint": "Capture des échantillons puis montre-les à la caméra", "output": "Sortie", "footPre": "Le modèle entraîné ici peut être utilisé dans les blocs ", "footExt": "Apprentissage AI", "footPost": " (prédiction, confiance, résumé) dans l'onglet [Code]. Fonctionne aussi avec le matériel !", "resetAllBtn": "Tout réinitialiser", "training": "Entraînement…", "trainDone": "Entraînement terminé", "trainReady": "Prêt — appuie sur Entraîner le modèle", "collectSamples": "Collecte des échantillons", "needMore": "Collecte au moins {n} photos par classe — avec plus d'exemples, l'AI apprend mieux", "lowSamples": "Prêt — mais plus d'échantillons = plus de précision ({n}+ par classe recommandé)", "trainErr": "L'entraînement a échoué — ajoute quelques échantillons et réessaie", "delClass": "Supprimer la classe", "delPhoto": "Supprimer la photo", "delConfirm": "Supprimer cette classe et toutes ses photos ?", "saveBtn": "Enregistrer", "loadBtn": "Ouvrir", "loadConfirm": "Ouvrir remplacera les classes et photos actuelles. Continuer ?", "loadingData": "Chargement des données…", "bTitle": "Mes modèles (ce navigateur)", "bSave": "Enregistrer dans le navigateur", "bNamePrompt": "Nom du modèle ?", "bOpen": "Ouvrir", "bDelete": "Supprimer", "bNone": "(aucun modèle enregistré)", "bDelConfirm": "Supprimer ce modèle du navigateur ?", "bHint": "Change de modèle dans [Code] avec le bloc « charger le modèle » !", "preparing": "Préparation des photos… {p}%", "maxReached": "Maximum {n} photos par classe", "mergeBtn": "Fusionner", "mergeHint": "Ajoute les classes de ce modèle aux actuelles (même nom = combinées)", "mergeConfirm": "Fusionner les classes de ce modèle avec les actuelles ?"},
    "it": {"title": "Apprendimento AI", "sub": "Insegna all'AI con la webcam → usala subito nei blocchi [Codice]", "loadingEngine": "Caricamento del motore AI…", "loadingCam": "Preparazione della webcam…", "klass": "Classe", "unitImgs": "img", "collectHint": "Premi il pulsante qui sotto (tieni premuto per acquisire in continuo) per raccogliere campioni", "capture": "Cattura dalla webcam", "upload": "Carica", "addClass": "＋ Aggiungi una classe", "trainTitle": "Addestramento", "trainBtn": "Addestra modello", "advanced": "Avanzate", "epochs": "Epoche", "batchSize": "Dimensione batch", "learningRate": "Tasso di apprendimento", "trainHint": "Rete neurale (transfer learning) — raccogli campioni e premi Addestra modello", "preview": "Anteprima", "nowSeeing": "Sta vedendo: ", "showHint": "Acquisisci campioni e poi mostrali alla telecamera", "output": "Uscita", "footPre": "Il modello addestrato qui può essere usato nei blocchi ", "footExt": "Apprendimento AI", "footPost": " (previsione, confidenza, riepilogo) nella scheda [Codice]. Funziona anche con l'hardware!", "resetAllBtn": "Reimposta tutto", "training": "Addestramento…", "trainDone": "Addestramento completato", "trainReady": "Pronto — premi Addestra modello", "collectSamples": "Raccogli campioni", "needMore": "Raccogli almeno {n} foto per classe — con più esempi l'AI impara meglio", "lowSamples": "Pronto — ma più campioni = più precisione (consigliati {n}+ per classe)", "trainErr": "Addestramento non riuscito — aggiungi qualche campione e riprova", "delClass": "Elimina classe", "delPhoto": "Elimina foto", "delConfirm": "Eliminare questa classe e tutte le sue foto?", "saveBtn": "Salva", "loadBtn": "Apri", "loadConfirm": "Aprendo sostituirai le classi e le foto attuali. Continuare?", "loadingData": "Caricamento dati…", "bTitle": "I miei modelli (questo browser)", "bSave": "Salva nel browser", "bNamePrompt": "Nome del modello?", "bOpen": "Apri", "bDelete": "Elimina", "bNone": "(nessun modello salvato)", "bDelConfirm": "Eliminare questo modello dal browser?", "bHint": "Cambia modello in [Codice] con il blocco «carica il modello»!", "preparing": "Preparazione foto… {p}%", "maxReached": "Massimo {n} foto per classe", "mergeBtn": "Unisci", "mergeHint": "Aggiungi le classi di questo modello a quelle attuali (stesso nome = combinate)", "mergeConfirm": "Unire le classi di questo modello a quelle attuali?"},
    "ru": {"title": "Обучение AI", "sub": "Обучите AI с помощью веб-камеры → сразу используйте в блоках на вкладке [Код]", "loadingEngine": "Загрузка движка AI…", "loadingCam": "Подготовка веб-камеры…", "klass": "Класс", "unitImgs": "изобр.", "collectHint": "Нажмите кнопку ниже (удерживайте для непрерывной съёмки), чтобы собрать образцы", "capture": "Снять с веб-камеры", "upload": "Загрузить", "addClass": "＋ Добавить класс", "trainTitle": "Обучение", "trainBtn": "Обучить модель", "advanced": "Дополнительно", "epochs": "Эпохи", "batchSize": "Размер пакета", "learningRate": "Скорость обучения", "trainHint": "Нейросеть (перенос обучения) — соберите образцы и нажмите «Обучить модель»", "preview": "Предпросмотр", "nowSeeing": "Сейчас видит: ", "showHint": "Сделайте образцы и покажите их камере", "output": "Вывод", "footPre": "Обученную здесь модель можно использовать в блоках ", "footExt": "Обучение AI", "footPost": " (предсказание, уверенность, сводка) на вкладке [Код]. Работает и с оборудованием!", "resetAllBtn": "Сбросить всё", "training": "Обучение…", "trainDone": "Обучение завершено", "trainReady": "Готово — нажмите «Обучить модель»", "collectSamples": "Соберите образцы", "needMore": "Соберите не менее {n} фото на класс — чем больше примеров, тем умнее AI", "lowSamples": "Готово — но больше образцов = выше точность (рекомендуется {n}+ на класс)", "trainErr": "Обучение не удалось — добавьте ещё несколько образцов и повторите", "delClass": "Удалить класс", "delPhoto": "Удалить фото", "delConfirm": "Удалить этот класс и все его фото?", "saveBtn": "Сохранить", "loadBtn": "Открыть", "loadConfirm": "Открытие заменит текущие классы и фото. Продолжить?", "loadingData": "Загрузка данных…", "bTitle": "Мои модели (этот браузер)", "bSave": "Сохранить в браузере", "bNamePrompt": "Название модели?", "bOpen": "Открыть", "bDelete": "Удалить", "bNone": "(нет сохранённых моделей)", "bDelConfirm": "Удалить эту модель из браузера?", "bHint": "Меняйте модели в [Код] блоком «загрузить модель»!", "preparing": "Подготовка фото… {p}%", "maxReached": "Максимум {n} фото на класс", "mergeBtn": "Объединить", "mergeHint": "Добавить классы этой модели к текущим (одинаковое имя = объединяются)", "mergeConfirm": "Объединить классы этой модели с текущими?"},
};

class AiTab extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, ['_drawLoop', '_poll', 'stopHold', 'addClass', 'resetAll', 'handleTrain', 'handleUpload',
            'handleDeleteThumb', 'handleDeleteClass', 'handleSave', 'handleLoad',
            'handleSaveBrowser', 'handleLoadBrowser', 'handleDeleteBrowser']);
        this.state = {
            status: '',
            ready: false,
            classes: [{label: '1', name: ''}, {label: '2', name: ''}],
            thumbs: {},
            counts: {},
            confidences: {},
            prediction: '',
            trainStatus: '',
            epochs: 20,
            batchSize: 0.4,
            learningRate: 0.0001,
            savedModels: [], // 보관함(브라우저 저장) 목록
            savedSel: ''
        };
        this.canvasRef = React.createRef();
        this._raf = null;
        this._pollTimer = null;
        this._holdTimer = null;
        this._video = null;
        this._thumbCanvas = document.createElement('canvas');
        this._thumbCanvas.width = 64;
        this._thumbCanvas.height = 48;
    }

    componentDidMount () {
        this._setup();
    }

    componentWillUnmount () {
        if (this._raf) cancelAnimationFrame(this._raf);
        if (this._pollTimer) clearInterval(this._pollTimer);
        this.stopHold();
    }

    // 현재 로케일에 맞는 문자열. ja-Hira는 ja로 폴백(영어보다 가까움), 그 외엔 en 폴백
    t (key) {
        let loc = (this.props.intl && this.props.intl.locale) || 'en';
        if (loc === 'ja-Hira') loc = 'ja';
        const ex = MSG_EXTRA[loc];
        if (ex && ex[key] != null) return ex[key];
        const m = MSG[key];
        if (!m) return key;
        return m[loc] || m.en || key;
    }
    // 인라인 SVG 아이콘 (이모지 대체) — currentColor 상속, 텍스트 앞에 붙임
    _ic (name, mr) {
        return (
            <span
                style={{display: 'inline-flex', marginRight: mr == null ? 5 : mr, fontSize: '1.05em', verticalAlign: '-0.15em'}}
                dangerouslySetInnerHTML={{__html: ICONS[name]}}
            />
        );
    }

    async _setup () {
        const vm = this.props.vm;
        if (!vm || !vm.runtime) return;
        try {
            if (!vm.extensionManager.isExtensionLoaded(ML_EXT)) {
                this.setState({status: this.t('loadingEngine')});
                await vm.extensionManager.loadExtensionURL(ML_EXT);
            }
        } catch (e) { /* noop */ }
        this._rehydrate();
        this._refreshSaved();
        try {
            this.setState({status: this.t('loadingCam')});
            await vm.runtime.ioDevices.video.enableVideo();
            const provider = vm.runtime.ioDevices.video.provider;
            this._video = provider && provider.video;
        } catch (e) { /* 웹캠 거부 */ }
        this.setState({ready: true, status: ''});
        this._drawLoop();
        this._pollTimer = setInterval(this._poll, 250);
    }

    _op (opcode) {
        const rt = this.props.vm && this.props.vm.runtime;
        return (rt && rt.getOpcodeFunction) ? rt.getOpcodeFunction(`${ML_EXT}_${opcode}`) : null;
    }

    // ⭐ VM에서 화면 상태 복원: 탭 전환(언마운트)·보관함 모델 열기 후 클래스·이름·카운트·썸네일을 맞춘다.
    //    (안 하면 "카드는 없는데 모델은 그 클래스를 예측"하는 유령 클래스 발생)
    _rehydrate () {
        try {
            const infoOp = this._op('getClassInfo');
            const list = infoOp ? infoOp() : null;
            if (!list || !list.length) return;
            const thumbsOp = this._op('getSampleThumbs');
            const classes = list.map(x => ({label: x.label, name: x.name || ''}));
            ['1', '2'].forEach(l => { // 최소 2칸은 유지(빈 기본 클래스)
                if (classes.length < 2 && !classes.some(c => c.label === l)) classes.push({label: l, name: ''});
            });
            classes.sort((a, b) => Number(a.label) - Number(b.label));
            const thumbs = {};
            const counts = {};
            list.forEach(x => {
                counts[x.label] = x.count;
                try {
                    const arr = thumbsOp ? (thumbsOp({LABEL: x.label}) || []) : [];
                    thumbs[x.label] = arr.slice(-MAX_THUMBS);
                } catch (e2) { thumbs[x.label] = []; }
            });
            this.setState({classes, thumbs, counts});
        } catch (e) { /* noop */ }
    }

    // 보관함(브라우저 저장) 목록 갱신
    _refreshSaved () {
        const ls = this._op('listModels');
        if (!ls) return;
        try {
            const p = ls();
            if (p && typeof p.then === 'function') {
                p.then(list => this.setState({savedModels: list || []})).catch(() => { /* noop */ });
            }
        } catch (e) { /* noop */ }
    }

    _drawLoop () {
        const canvas = this.canvasRef.current;
        if (canvas && this._video && this._video.videoWidth) {
            const ctx = canvas.getContext('2d');
            ctx.save();
            ctx.scale(-1, 1);
            ctx.drawImage(this._video, -canvas.width, 0, canvas.width, canvas.height);
            ctx.restore();
        }
        this._raf = requestAnimationFrame(this._drawLoop);
    }

    _poll () {
        const counts = {};
        const confs = {};
        const cf = this._op('getCountByLabel');
        const gc = this._op('getConfidence');
        this.state.classes.forEach(c => {
            const l = c.label;
            try { counts[l] = cf ? (cf({LABEL: l}) || 0) : 0; } catch (e) { counts[l] = 0; }
            try { confs[l] = gc ? (gc({LABEL: l}) || 0) : 0; } catch (e) { confs[l] = 0; }
        });
        let prediction = '';
        const gp = this._op('getPrediction');
        try { prediction = gp ? (gp() || '') : ''; } catch (e) { prediction = ''; }
        let trainStatus = '';
        const gs = this._op('getTrainStatus');
        try { trainStatus = gs ? (gs() || '') : ''; } catch (e) { trainStatus = ''; }
        // 🎉 학습이 막 끝난 순간(done 으로 처음 바뀔 때) 축하 — 폴링이라 이전 값과 비교해야 매번 안 뜬다
        if (trainStatus === 'done' && this._prevTrainStatus !== 'done') cheer('aiTrained');
        // 🎯 학습한 모델이 실제로 무언가를 알아본 첫 순간
        if (prediction && !this._sawPrediction) { this._sawPrediction = true; cheer('aiPredict'); }
        this._prevTrainStatus = trainStatus;
        this.setState({counts, confidences: confs, prediction, trainStatus});
    }

    // 확장이 준 상태 코드(training:pct:loss / done / ready / empty)를 현지화
    _fmtTrainStatus (code) {
        if (!code) return this.t('collectSamples');
        if (code === 'done') return this.t('trainDone');
        if (code === 'ready') return this.t('trainReady');
        if (code === 'empty') return this.t('collectSamples');
        if (code === 'diverged') return this.t('trainDiverged');
        if (code === 'error') return this.t('trainErr');
        if (code.indexOf('need:') === 0) {
            return this.t('needMore').replace('{n}', code.split(':')[1] || '5');
        }
        if (code.indexOf('low:') === 0) {
            return this.t('lowSamples').replace('{n}', code.split(':')[1] || '20');
        }
        if (code.indexOf('preparing:') === 0) {
            return this.t('preparing').replace('{p}', code.split(':')[1] || '0');
        }
        if (code.indexOf('training:') === 0) {
            const parts = code.split(':');
            return `${this.t('training')} ${parts[1] || '0'}% (loss ${parts[2] || '-'})`;
        }
        return code;
    }

    handleTrain () {
        const train = this._op('trainModel');
        if (!train) return;
        try {
            train({
                EPOCHS: this.state.epochs,
                BATCH_SIZE: this.state.batchSize,
                LEARNING_RATE: this.state.learningRate
            });
        } catch (e) { /* noop */ }
    }

    _grabThumb () {
        if (!this._video || !this._video.videoWidth) return null;
        const c = this._thumbCanvas;
        const ctx = c.getContext('2d');
        ctx.save();
        ctx.scale(-1, 1);
        ctx.drawImage(this._video, -c.width, 0, c.width, c.height);
        ctx.restore();
        try { return c.toDataURL('image/jpeg', 0.5); } catch (e) { return null; }
    }

    // 클래스당 상한 도달 안내 (2초 표시) — ⚠️ '50'은 VM ml2scratch2의 maxSamples와 함께 변경
    _flashMax () {
        this.setState({status: this.t('maxReached').replace('{n}', '50')});
        if (this._maxTimer) clearTimeout(this._maxTimer);
        this._maxTimer = setTimeout(() => this.setState({status: ''}), 2000);
    }

    captureOnce (label) {
        const train = this._op('train');
        if (!train) return;
        let id = -1;
        try { id = train({LABEL: label}); } catch (e) { return; }
        if (id === -2) { this._flashMax(); this.stopHold(); return; }
        const thumb = this._grabThumb();
        if (thumb && id > 0) { // id = VM 샘플 id (썸네일 ✕ 삭제와 짝)
            this.setState(prev => {
                const arr = (prev.thumbs[label] || []).concat({id, src: thumb});
                while (arr.length > MAX_THUMBS) arr.shift();
                return {thumbs: Object.assign({}, prev.thumbs, {[label]: arr})};
            });
        }
    }

    // 파일 업로드 샘플 추가: 이미지 파일 → Image 요소 → 학습(train opcode에 IMG 전달) + 썸네일
    handleUpload (label, fileList) {
        const train = this._op('train');
        if (!train || !fileList) return;
        Array.from(fileList).forEach(file => {
            if (!file.type || file.type.indexOf('image') !== 0) return;
            const reader = new FileReader();
            reader.onload = e => {
                const src = e.target.result;
                const img = new Image();
                img.onload = () => {
                    let id = -1;
                    try { id = train({LABEL: label, IMG: img}); } catch (er) { /* noop */ }
                    if (id === -2) { this._flashMax(); return; }
                    if (id > 0) {
                        this.setState(prev => {
                            const arr = (prev.thumbs[label] || []).concat({id, src});
                            while (arr.length > MAX_THUMBS) arr.shift();
                            return {thumbs: Object.assign({}, prev.thumbs, {[label]: arr})};
                        });
                    }
                };
                img.src = src;
            };
            reader.readAsDataURL(file);
        });
    }

    startHold (label) {
        this.stopHold();
        this.captureOnce(label);
        // 연속 촬영 간격을 이 PC의 실측 추론 속도에 맞춤 — 느린 PC에서 촬영이 처리 속도보다
        // 빨리 쌓여 브라우저가 버벅이는 것 방지(빠른 PC는 기본 130ms 그대로)
        let iv = 130;
        const perf = this._op('getPerfInfo');
        try {
            const p = perf ? perf() : null;
            if (p && p.inferMs) iv = Math.max(130, Math.round(p.inferMs * 1.2));
        } catch (e) { /* noop */ }
        this._holdTimer = setInterval(() => this.captureOnce(label), iv);
    }

    stopHold () {
        if (this._holdTimer) { clearInterval(this._holdTimer); this._holdTimer = null; }
    }

    // 사진 1장 삭제 (썸네일 ✕) — VM이 남은 스냅샷으로 분류기 재구성, 학습상태는 poll이 갱신
    handleDeleteThumb (label, id) {
        const del = this._op('deleteSample');
        if (del) { try { del({LABEL: label, ID: id}); } catch (e) { /* noop */ } }
        this.setState(prev => ({
            thumbs: Object.assign({}, prev.thumbs, {[label]: (prev.thumbs[label] || []).filter(t => t.id !== id)})
        }));
    }

    // 클래스 통째 삭제 (카드 🗑) — 실수 방지 confirm, 마지막 1개는 못 지움
    handleDeleteClass (label) {
        if (this.state.classes.length <= 1) return;
        if (!window.confirm(this.t('delConfirm'))) return;
        const del = this._op('deleteClass');
        if (del) { try { del({LABEL: label}); } catch (e) { /* noop */ } }
        this.setState(prev => {
            const thumbs = Object.assign({}, prev.thumbs);
            const counts = Object.assign({}, prev.counts);
            const confidences = Object.assign({}, prev.confidences);
            delete thumbs[label]; delete counts[label]; delete confidences[label];
            return {classes: prev.classes.filter(c => c.label !== label), thumbs, counts, confidences};
        });
    }

    // 학습 데이터 저장 — 전 클래스의 사진(224px)+이름+고급설정을 JSON 파일로 다운로드
    handleSave () {
        const ex = this._op('exportData');
        if (!ex) return;
        let classData = [];
        try { classData = ex() || []; } catch (e) { return; }
        if (!classData.length) return; // 저장할 샘플 없음
        const data = {
            type: 'curoai-ai-learning',
            version: 1,
            settings: {epochs: this.state.epochs, batchSize: this.state.batchSize, learningRate: this.state.learningRate},
            classes: classData
        };
        const blob = new Blob([JSON.stringify(data)], {type: 'application/json'});
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const d = new Date();
        const pad = n => `0${n}`.slice(-2);
        a.download = `curoai-ai-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}.json`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    }

    // 파일 열기 — 파싱·검증 후 공통 적용기로. (파일은 항상 교체)
    handleLoad (file, merge) {
        const reader = new FileReader();
        reader.onload = e => {
            let data;
            try { data = JSON.parse(e.target.result); } catch (er) { return; }
            if (!data || data.type !== 'curoai-ai-learning' || !Array.isArray(data.classes)) return;
            if (this.state.trainStatus && this.state.trainStatus.indexOf('training:') === 0) return;
            if (!window.confirm(this.t(merge ? 'mergeConfirm' : 'loadConfirm'))) return;
            this._applyModelData(data, merge);
        };
        reader.readAsText(file);
    }

    // 파일/보관함 모델 데이터를 화면+VM에 반영.
    //  merge=false → 교체(기존 비움) / merge=true → 합침: 이름 같은 클래스엔 사진 추가, 다르면 새 클래스로.
    //  (두 모델을 한 모델로 합쳐 "동시 인식", 또는 선생님 기본모델 + 학생 클래스 추가)
    _applyModelData (data, merge) {
        if (!data || !Array.isArray(data.classes) || !data.classes.length) return;
        const train = this._op('train');
        if (!train) return;
        if (!merge) {
            const clear = this._op('clearAll');
            if (clear) { try { clear(); } catch (e) { /* noop */ } }
        }
        const setName = this._op('setLabelName');
        // 대상 클래스: merge면 데이터 있는 현재 클래스 유지, 아니면 빈 상태
        const classes = merge ?
            this.state.classes.filter(c => c.name || (this.state.counts[c.label] || 0) > 0) : [];
        const byName = {};
        classes.forEach(c => { if (c.name) byName[c.name] = c.label; });
        const used = new Set(classes.map(c => Number(c.label)));
        const nextLabel = () => { let n = 1; while (used.has(n)) n++; used.add(n); return String(n); };
        const jobs = [];
        data.classes.forEach(c => {
            const name = c.name || '';
            let label;
            if (name && byName[name]) {
                label = byName[name]; // 같은 이름 → 그 클래스에 합침
            } else {
                if (classes.length >= MAX_CLASSES) return; // 상한 초과 → 스킵
                label = nextLabel();
                classes.push({label, name});
                if (name) byName[name] = label;
            }
            if (name && setName) { try { setName({NUMBER: label, NAME: name}); } catch (e) { /* noop */ } }
            (Array.isArray(c.samples) ? c.samples : []).forEach(src => {
                if (typeof src !== 'string' || src.indexOf('data:image') !== 0) return;
                jobs.push(new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { try { train({LABEL: label, IMG: img}); } catch (e) { /* noop */ } resolve(); };
                    img.onerror = () => resolve();
                    img.src = src;
                }));
            });
        });
        ['1', '2'].forEach(l => { if (classes.length < 2 && !classes.some(c => c.label === l)) classes.push({label: l, name: ''}); });
        classes.sort((a, b) => Number(a.label) - Number(b.label));
        const st = data.settings || {};
        const patch = {classes, status: this.t('loadingData')};
        if (!merge) {
            Object.assign(patch, {thumbs: {}, counts: {}, confidences: {}, prediction: '',
                epochs: st.epochs || this.state.epochs,
                batchSize: st.batchSize || this.state.batchSize,
                learningRate: st.learningRate || this.state.learningRate});
        }
        this.setState(patch);
        Promise.all(jobs).then(() => {
            this.setState({status: '', thumbs: this._loadThumbs(classes)});
            this.handleTrain(); // 열자마자 학습
        });
    }

    // VM 스냅샷에서 각 클래스 썸네일 [{id, src}] 재생성 (열기/병합 후 스트립 표시)
    _loadThumbs (classes) {
        const thumbsOp = this._op('getSampleThumbs');
        const thumbs = {};
        classes.forEach(c => {
            try { thumbs[c.label] = thumbsOp ? (thumbsOp({LABEL: c.label}) || []).slice(-MAX_THUMBS) : []; }
            catch (e) { thumbs[c.label] = []; }
        });
        return thumbs;
    }

    // 보관함(브라우저)에 이름 붙여 저장
    handleSaveBrowser () {
        const save = this._op('saveModelAs');
        if (!save) return;
        const first = this.state.classes.find(c => c.name);
        const suggest = (first && first.name) || '';
        const name = window.prompt(this.t('bNamePrompt'), suggest);
        if (!name || !name.trim()) return;
        try {
            const p = save({NAME: name.trim()});
            if (p && typeof p.then === 'function') p.then(() => this._refreshSaved()).catch(() => { /* noop */ });
        } catch (e) { /* noop */ }
    }

    // 📥 보관함 모델 열기(merge=false) / 합치기(merge=true) — 데이터를 읽어 공통 적용기로
    handleLoadBrowser (merge) {
        const {savedModels, savedSel, trainStatus} = this.state;
        const name = savedSel || (savedModels[0] && savedModels[0].name) || '';
        if (!name) return;
        if (trainStatus && trainStatus.indexOf('training:') === 0) return; // 학습 중 금지
        if (!window.confirm(this.t(merge ? 'mergeConfirm' : 'loadConfirm'))) return;
        const get = this._op('getModelData');
        if (!get) return;
        this.setState({status: this.t('loadingData')});
        const go = record => {
            if (record && Array.isArray(record.classes)) this._applyModelData(record, merge);
            else this.setState({status: ''});
        };
        try {
            const p = get({NAME: name});
            if (p && typeof p.then === 'function') p.then(go).catch(() => this.setState({status: ''}));
            else go(p);
        } catch (e) { this.setState({status: ''}); }
    }

    // 🗑 보관함에서 모델 삭제
    handleDeleteBrowser () {
        const {savedModels, savedSel} = this.state;
        const name = savedSel || (savedModels[0] && savedModels[0].name) || '';
        if (!name) return;
        if (!window.confirm(this.t('bDelConfirm'))) return;
        const del = this._op('deleteModel');
        if (!del) return;
        try {
            const p = del({NAME: name});
            const after = () => { this.setState({savedSel: ''}); this._refreshSaved(); };
            if (p && typeof p.then === 'function') p.then(after).catch(after);
            else after();
        } catch (e) { /* noop */ }
    }

    handleName (label, value) {
        this.setState(prev => ({
            classes: prev.classes.map(c => (c.label === label ? Object.assign({}, c, {name: value}) : c))
        }));
        const setName = this._op('setLabelName');
        if (setName) { try { setName({NUMBER: label, NAME: value}); } catch (e) { /* noop */ } }
    }

    addClass () {
        this.setState(prev => {
            if (prev.classes.length >= MAX_CLASSES) return null;
            const used = prev.classes.map(c => parseInt(c.label, 10));
            let n = 1;
            while (used.indexOf(n) !== -1) n++;
            return {classes: prev.classes.concat({label: String(n), name: ''})};
        });
    }

    resetAll () {
        const reset = this._op('reset');
        if (reset) { try { reset({LABEL: 'all'}); } catch (e) { /* noop */ } }
        this.setState({
            thumbs: {}, counts: {}, confidences: {}, prediction: '',
            classes: [{label: '1', name: ''}, {label: '2', name: ''}]
        });
    }

    render () {
        const {status, ready, classes, thumbs, counts, confidences, prediction,
            trainStatus, epochs, batchSize, learningRate} = this.state;
        let topLabel = null;
        let topConf = 0;
        classes.forEach(c => { if ((confidences[c.label] || 0) > topConf) { topConf = confidences[c.label]; topLabel = c.label; } });
        const trained = classes.some(c => (counts[c.label] || 0) > 0);

        const holdHandlers = label => ({
            onMouseDown: () => this.startHold(label),
            onMouseUp: this.stopHold,
            onMouseLeave: this.stopHold,
            onTouchStart: e => { e.preventDefault(); this.startHold(label); },
            onTouchEnd: this.stopHold
        });

        return (
            <div style={S.wrap}>
                <div style={S.header}>
                    <span style={S.title}>{this._ic('brain', 8)}{this.t('title')}</span>
                    <span style={S.sub}>{this.t('sub')}</span>
                    {status ? <span style={S.status}>{status}</span> : null}
                </div>

                <div style={S.body}>
                    {/* 왼쪽: 클래스 카드들 */}
                    <div style={S.cards}>
                        {classes.map((c, i) => {
                            const color = PALETTE[i % PALETTE.length];
                            const list = thumbs[c.label] || [];
                            return (
                                <div key={c.label} style={S.card}>
                                    <div style={S.cardHead}>
                                        <span style={Object.assign({}, S.dot, {background: color})} />
                                        <input
                                            style={S.nameInput}
                                            value={c.name}
                                            placeholder={`${this.t('klass')} ${c.label}`}
                                            maxLength={20}
                                            onChange={e => this.handleName(c.label, e.target.value)}
                                        />
                                        <span style={S.count}>{`${counts[c.label] || 0} ${this.t('unitImgs')}`}</span>
                                        {classes.length > 1 ?
                                            <span
                                                style={S.delClassBtn}
                                                title={this.t('delClass')}
                                                onClick={() => this.handleDeleteClass(c.label)}
                                                dangerouslySetInnerHTML={{__html: ICONS.trash}}
                                            /> : null}
                                    </div>
                                    <div style={S.thumbStrip}>
                                        {list.length === 0 ?
                                            <span style={S.thumbHint}>{this.t('collectHint')}</span> :
                                            list.map(tItem => (
                                                <span key={tItem.id} style={S.thumbWrap}>
                                                    <img src={tItem.src} style={S.thumb} draggable={false} />
                                                    <span
                                                        style={S.thumbDel}
                                                        title={this.t('delPhoto')}
                                                        onClick={() => this.handleDeleteThumb(c.label, tItem.id)}
                                                    >{'×'}</span>
                                                </span>
                                            ))
                                        }
                                    </div>
                                    <div style={S.capRow}>
                                        <button
                                            style={Object.assign({}, S.capBtn, {background: ready ? color : '#c6cbd6'})}
                                            disabled={!ready}
                                            {...holdHandlers(c.label)}
                                        >
                                            {this._ic('camera', 5)}{this.t('capture')}
                                        </button>
                                        <label style={Object.assign({}, S.uploadBtn, ready ? null : S.btnDisabled)}>
                                            {this._ic('upload', 5)}{this.t('upload')}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                disabled={!ready}
                                                style={{display: 'none'}}
                                                onChange={e => { this.handleUpload(c.label, e.target.files); e.target.value = ''; }}
                                            />
                                        </label>
                                    </div>
                                </div>
                            );
                        })}
                        {classes.length < MAX_CLASSES ?
                            <button style={S.addBtn} onClick={this.addClass}>{this.t('addClass')}</button> : null}
                    </div>

                    {/* 가운데: 학습 (신경망 학습) */}
                    <div style={S.trainPanel}>
                        <div style={S.tpTitle}>{this.t('trainTitle')}</div>
                        <button
                            style={Object.assign({}, S.trainBtn, ready ? null : S.btnDisabled)}
                            disabled={!ready}
                            onClick={this.handleTrain}
                        >
                            {this.t('trainBtn')}
                        </button>
                        <div style={S.tpStatus}>{this._fmtTrainStatus(trainStatus)}</div>
                        <div style={S.tpAdvTitle}>{this.t('advanced')}</div>
                        <div style={S.tpRow}>
                            <span style={S.tpLabel}>{this.t('epochs')}</span>
                            <input
                                type="number" min={1} max={200} value={epochs} style={S.tpInput}
                                onChange={e => this.setState({epochs: Math.max(1, parseInt(e.target.value, 10) || 1)})}
                            />
                        </div>
                        <div style={S.tpRow}>
                            <span style={S.tpLabel}>{this.t('batchSize')}</span>
                            <input
                                type="number" min={0.1} max={1} step={0.1} value={batchSize} style={S.tpInput}
                                onChange={e => this.setState({batchSize: parseFloat(e.target.value) || 0.4})}
                            />
                        </div>
                        <div style={S.tpRow}>
                            <span style={S.tpLabel}>{this.t('learningRate')}</span>
                            <input
                                type="number" min={0.00001} step={0.0001} value={learningRate} style={S.tpInput}
                                onChange={e => this.setState({learningRate: parseFloat(e.target.value) || 0.0001})}
                            />
                        </div>
                        <div style={S.tpHint}>{this.t('trainHint')}</div>
                        <div style={S.tpAdvTitle}>{this.t('fileTitle')}</div>
                        <div style={S.dataRow}>
                            <button
                                style={Object.assign({}, S.dataBtn, ready ? null : S.btnDisabled)}
                                disabled={!ready}
                                title={this.t('fileHint')}
                                onClick={this.handleSave}
                            >{this._ic('disk', 5)}{this.t('saveBtn')}</button>
                            <label
                                style={Object.assign({}, S.dataBtn, ready ? null : S.btnDisabled)}
                                title={this.t('fileHint')}
                            >
                                {this._ic('folder', 5)}{this.t('loadBtn')}
                                <input
                                    type="file"
                                    accept=".json,application/json"
                                    disabled={!ready}
                                    style={{display: 'none'}}
                                    onChange={e => {
                                        if (e.target.files && e.target.files[0]) this.handleLoad(e.target.files[0]);
                                        e.target.value = '';
                                    }}
                                />
                            </label>
                        </div>
                        <div style={S.tpHint}>{this.t('fileHint')}</div>
                        {/* 내 모델 (브라우저 보관함, IndexedDB) — 코드 탭 loadModel 블록과 연동 */}
                        <div style={S.tpAdvTitle}>{this.t('bTitle')}</div>
                        <button
                            style={Object.assign({}, S.dataBtn, {width: '100%'}, ready ? null : S.btnDisabled)}
                            disabled={!ready}
                            title={this.t('bInfo')}
                            onClick={this.handleSaveBrowser}
                        >{this._ic('browser', 5)}{this.t('bSave')}</button>
                        <div style={S.tpHint}>{this.t('bInfo')}</div>
                        <div style={S.dataRow}>
                            <select
                                style={S.bSelect}
                                value={this.state.savedSel || (this.state.savedModels[0] && this.state.savedModels[0].name) || ''}
                                onChange={e => this.setState({savedSel: e.target.value})}
                            >
                                {this.state.savedModels.length === 0 ?
                                    <option value="">{this.t('bNone')}</option> :
                                    this.state.savedModels.map(m => (
                                        <option key={m.name} value={m.name}>{m.name}</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div style={S.dataRow}>
                            <button
                                style={Object.assign({}, S.dataBtn, (ready && this.state.savedModels.length) ? null : S.btnDisabled)}
                                disabled={!ready || !this.state.savedModels.length}
                                onClick={() => this.handleLoadBrowser(false)}
                            >{this.t('bOpen')}</button>
                            <button
                                style={Object.assign({}, S.dataBtn, (ready && this.state.savedModels.length) ? null : S.btnDisabled)}
                                disabled={!ready || !this.state.savedModels.length}
                                title={this.t('mergeHint')}
                                onClick={() => this.handleLoadBrowser(true)}
                            >{this._ic('merge', 4)}{this.t('mergeBtn')}</button>
                            <button
                                style={Object.assign({}, S.dataBtn, (ready && this.state.savedModels.length) ? null : S.btnDisabled)}
                                disabled={!ready || !this.state.savedModels.length}
                                onClick={this.handleDeleteBrowser}
                            >{this.t('bDelete')}</button>
                        </div>
                        <div style={S.tpHint}>{this.t('bHint')}</div>
                    </div>

                    {/* 오른쪽: 미리보기(웹캠 + 출력 바) */}
                    <div style={S.preview}>
                        <div style={S.pvTitle}>{this.t('preview')}</div>
                        <canvas ref={this.canvasRef} width={280} height={210} style={S.canvas} />
                        <div style={S.pvPredict}>
                            {trained ?
                                <span>{this.t('nowSeeing')}<b>{prediction || '…'}</b></span> :
                                <span style={{opacity: 0.6}}>{this.t('showHint')}</span>}
                        </div>
                        <div style={S.pvOutTitle}>{this.t('output')}</div>
                        {classes.map((c, i) => {
                            const color = PALETTE[i % PALETTE.length];
                            const pct = confidences[c.label] || 0;
                            const isTop = trained && c.label === topLabel && pct > 0;
                            return (
                                <div key={c.label} style={S.outRow}>
                                    <span style={Object.assign({}, S.outName, isTop ? {fontWeight: 'bold', color} : null)}>
                                        {c.name || `${this.t('klass')} ${c.label}`}
                                    </span>
                                    <div style={S.outTrack}>
                                        <div style={Object.assign({}, S.outFill, {width: `${pct}%`, background: color})} />
                                    </div>
                                    <span style={S.outPct}>{`${pct}%`}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div style={S.foot}>
                    <span>
                        {this.t('footPre')}<b>{this.t('footExt')}</b>{this.t('footPost')}
                    </span>
                    <button style={S.resetBtn} onClick={this.resetAll}>{this.t('resetAllBtn')}</button>
                </div>
            </div>
        );
    }
}

const S = {
    wrap: {height: '100%', minHeight: 380, display: 'flex', flexDirection: 'column', padding: 16,
        background: '#fff', border: '1px solid rgba(0, 0, 0, 0.15)', borderRadius: '0 8px 8px 8px',
        boxSizing: 'border-box', color: '#575e75', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        overflowY: 'auto', overflowX: 'hidden', width: '100%', maxWidth: '100%'},
    header: {display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 10, marginBottom: 12},
    title: {fontSize: 22, fontWeight: 'bold'},
    sub: {fontSize: 13, opacity: 0.7},
    status: {fontSize: 13, color: '#4c97ff'},
    body: {display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start', flex: 1, minWidth: 0},
    cards: {display: 'flex', flexDirection: 'column', gap: 12, flex: '1 1 260px', minWidth: 200, maxWidth: 520},
    card: {border: '1px solid #e6e8ef', borderRadius: 12, padding: 12, background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'},
    cardHead: {display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8},
    dot: {width: 12, height: 12, borderRadius: 6, flex: '0 0 auto'},
    nameInput: {border: '1px solid #d9dce3', borderRadius: 6, padding: '5px 8px', fontSize: 14,
        flex: 1, color: '#575e75', minWidth: 0},
    count: {fontSize: 12, opacity: 0.65, flex: '0 0 auto'},
    thumbStrip: {display: 'flex', gap: 4, overflowX: 'auto', minHeight: 48, alignItems: 'center',
        background: '#f6f7fb', borderRadius: 8, padding: 6, marginBottom: 8},
    thumbHint: {fontSize: 12, opacity: 0.5, padding: '0 4px'},
    thumb: {width: 48, height: 36, objectFit: 'cover', borderRadius: 4, display: 'block'},
    thumbWrap: {position: 'relative', flex: '0 0 auto'},
    thumbDel: {position: 'absolute', top: 1, right: 1, width: 14, height: 14, lineHeight: '13px',
        borderRadius: '50%', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11,
        textAlign: 'center', cursor: 'pointer', userSelect: 'none'},
    delClassBtn: {fontSize: 13, cursor: 'pointer', flex: '0 0 auto', opacity: 0.55, userSelect: 'none', display: 'inline-flex', alignItems: 'center'},
    capRow: {display: 'flex', gap: 6},
    capBtn: {flex: 2, border: 'none', borderRadius: 8, padding: '9px 0', fontSize: 14,
        fontWeight: 'bold', color: '#fff', cursor: 'pointer', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center'},
    uploadBtn: {flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid #d9dce3', borderRadius: 8, padding: '9px 4px', fontSize: 13,
        color: '#575e75', background: '#fff', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap'},
    addBtn: {border: '2px dashed #c6cbd6', borderRadius: 12, padding: '12px 0', fontSize: 14,
        color: '#7b8194', background: 'transparent', cursor: 'pointer'},
    preview: {flex: '0 1 300px', minWidth: 240, maxWidth: 340, border: '1px solid #e6e8ef', borderRadius: 12,
        padding: 14, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'},
    pvTitle: {fontSize: 16, fontWeight: 'bold', marginBottom: 10},
    canvas: {width: '100%', maxWidth: 260, height: 'auto', background: '#000', borderRadius: 10, display: 'block'},
    pvPredict: {textAlign: 'center', fontSize: 14, padding: '10px 0', margin: '8px 0',
        background: '#f2f6ff', borderRadius: 8},
    pvOutTitle: {fontSize: 13, fontWeight: 'bold', margin: '6px 0 8px'},
    outRow: {display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7},
    outName: {fontSize: 13, width: 84, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        flex: '0 0 auto'},
    outTrack: {position: 'relative', height: 14, background: '#eef0f4', borderRadius: 7, overflow: 'hidden', flex: 1},
    outFill: {height: '100%', borderRadius: 7, transition: 'width .2s'},
    outPct: {fontSize: 11, width: 34, textAlign: 'right', flex: '0 0 auto'},
    foot: {marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, flexWrap: 'wrap', fontSize: 13, opacity: 0.9, lineHeight: 1.5},
    resetBtn: {border: '1px solid #ff8c8c', color: '#e64a4a', background: '#fff', borderRadius: 8,
        padding: '6px 14px', fontSize: 13, cursor: 'pointer', flex: '0 0 auto'},
    btnDisabled: {background: '#c6cbd6', cursor: 'default'},
    trainPanel: {flex: '0 0 180px', minWidth: 160, border: '1px solid #e6e8ef', borderRadius: 12,
        padding: 14, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'},
    tpTitle: {fontSize: 16, fontWeight: 'bold', marginBottom: 10},
    trainBtn: {width: '100%', border: 'none', borderRadius: 8, padding: '10px 0', fontSize: 14,
        fontWeight: 'bold', color: '#fff', background: '#40bf4a', cursor: 'pointer'},
    tpStatus: {fontSize: 12, textAlign: 'center', padding: '8px 0', color: '#575e75', minHeight: 16},
    tpAdvTitle: {fontSize: 13, fontWeight: 'bold', margin: '8px 0 6px', color: '#4c97ff'},
    tpRow: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8},
    tpLabel: {fontSize: 13},
    tpInput: {width: 74, border: '1px solid #d9dce3', borderRadius: 6, padding: '4px 6px', fontSize: 13,
        color: '#575e75'},
    tpHint: {fontSize: 12, opacity: 0.8, marginTop: 6, lineHeight: 1.45},
    dataRow: {display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10},
    dataBtn: {flex: '1 1 auto', whiteSpace: 'nowrap', padding: '6px 6px', fontSize: 12, border: '1px solid #d9dce4', borderRadius: 6,
        background: '#fff', cursor: 'pointer', textAlign: 'center', userSelect: 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center'},
    bSelect: {flex: 1, fontSize: 12, padding: '4px', border: '1px solid #d9dce4', borderRadius: 6,
        background: '#fff', maxWidth: '100%'}
};

AiTab.propTypes = {
    vm: PropTypes.object,
    intl: PropTypes.object
};

export default injectIntl(AiTab);
